import {
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Text,
} from '@fluentui/react-components';
import type { MyTenant, MyTenantsResponse } from '@warehouselogix/shared';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { DataTable } from '../components/DataTable';
import { EmptyState, ErrorBar, LoadingState, Screen } from '../components/ScreenChrome';

import type { PickerState } from './CurrentTenantContext';
import { RoleBadge, ViaBadge } from './RoleBadge';
import { readLastTenant, tenantPath, useMyTenants } from './useMyTenants';

/** First screen under `/t/{slug}` when a tenant is opened. */
const LANDING_SCREEN = 'site-config';

/**
 * Where `/` should send the caller without asking (PLAN §5), or null to show
 * the list:
 *   - exactly one tenant and not a platform admin → that tenant;
 *   - the session's last tenant, when still listed → that tenant.
 * Pure so the rule is readable (and testable) on its own.
 */
export function pickerRedirect(data: MyTenantsResponse, lastTenant: string | null): string | null {
  const only = data.tenants.length === 1 ? data.tenants[0] : undefined;
  if (only !== undefined && !data.isPlatformAdmin) {
    return only.slug;
  }
  if (lastTenant !== null && data.tenants.some((t) => t.slug === lastTenant)) {
    return lastTenant;
  }
  return null;
}

/**
 * "Choose a tenant" — the `/` route. Lists `GET /me/tenants` with role and
 * access badges. Auto-redirects per {@link pickerRedirect} UNLESS the caller
 * arrived here on purpose (nav tab) or was bounced here with a message — in
 * both cases the list, not a redirect, is the point.
 */
export function TenantPicker(): JSX.Element {
  const my = useMyTenants();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? null) as PickerState | null;
  const message = state?.message ?? null;
  const suppressRedirect = message !== null || state?.explicit === true;

  const redirectTo =
    my.data !== null && !suppressRedirect ? pickerRedirect(my.data, readLastTenant()) : null;

  useEffect(() => {
    if (redirectTo !== null) {
      void navigate(tenantPath(redirectTo, LANDING_SCREEN), { replace: true });
    }
  }, [redirectTo, navigate]);

  const open = (t: MyTenant): void => {
    void navigate(tenantPath(t.slug, LANDING_SCREEN));
  };

  return (
    <Screen
      title="Choose a tenant"
      subtitle="Tenants this account can administer."
      actions={
        <Button appearance="secondary" onClick={my.reload} disabled={my.loading}>
          Refresh
        </Button>
      }
    >
      {message !== null ? (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>No access</MessageBarTitle>
            {message}
          </MessageBarBody>
        </MessageBar>
      ) : null}
      {my.error !== null && my.data === null ? (
        <ErrorBar message={my.error} onRetry={my.reload} />
      ) : null}
      {my.data === null && my.loading ? <LoadingState label="Loading your tenants…" /> : null}
      {redirectTo !== null ? <LoadingState label="Opening…" /> : null}
      {my.data !== null && redirectTo === null ? (
        my.data.tenants.length === 0 ? (
          <EmptyState message="No tenants — ask your administrator" />
        ) : (
          <DataTable
            columns={[
              {
                columnId: 'name',
                header: 'Tenant',
                renderCell: (t: MyTenant) => <Text weight="semibold">{t.name}</Text>,
              },
              { columnId: 'slug', header: 'Slug', renderCell: (t: MyTenant) => t.slug },
              {
                columnId: 'role',
                header: 'Role',
                renderCell: (t: MyTenant) => <RoleBadge role={t.role} />,
              },
              {
                columnId: 'via',
                header: 'Access',
                renderCell: (t: MyTenant) => <ViaBadge via={t.via} />,
              },
              {
                columnId: 'actions',
                header: '',
                renderCell: (t: MyTenant) => (
                  <Button size="small" appearance="primary" onClick={() => open(t)}>
                    Open
                  </Button>
                ),
              },
            ]}
            rows={my.data.tenants}
            getRowId={(t) => t.id}
            emptyMessage="No tenants — ask your administrator"
          />
        )
      ) : null}
    </Screen>
  );
}
